<template>
  <div class="container-fluid">
    <div class="contents">
      <!-- Page Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h2 class="tx-18 tx-semibold mg-b-0">Pricing Configurations</h2>
        <button class="btn btn-primary" @click="openCreateModal">Create Pricing</button>
      </div>
      <CustomTable
        :tableFields="pricingPlans.table.table_fields"
        :tableData="pricingData"
        :title="pricingPlans.table.title"
        :subtitle="pricingPlans.table.subtitle"
        :searchFields="pricingPlans.table.searchFields"
        :rowData="pricingPlans.table.rowData"
      >
      </CustomTable>
    </div>

    <!-- Create Modal -->
    <div
      class=""
      :class="{ show: showCreateModal }"
      :style="{ display: showCreateModal ? 'block' : 'none' }"
      tabindex="-1"
      role="dialog"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Create Pricing</h5>
            <button type="button" class="close" @click="closeCreateModal">&times;</button>
          </div>
          <div class="modal-body">
            <form>
              <div class="form-group">
                <label>Plan Name</label>
                <input v-model="newPricing.planName" type="text" class="form-control" placeholder="Enter plan name">
              </div>
              <div class="form-group">
                <label>Description</label>
                <textarea v-model="newPricing.planDescription" class="form-control" rows="3" placeholder="Enter description"></textarea>
              </div>
              <div class="form-group">
                <label>Plan Type</label>
                <select v-model="newPricing.planType" class="form-control" @change="updatePlanType">
                  <option value="FREE">Free</option>
                  <option value="PAID">Paid</option>
                </select>
              </div>
              <div class="form-group">
                <label>Entity</label>
                <select v-model="newPricing.planUserType" class="form-control">
                  <option value="INTERMEDIARY">Intermediary</option>
                  <option value="UNDERWRITER">Underwriter</option>
                  <option value="CLIENT">Client</option>
                  <option value="SERVICE_PROVIDER">Service Provider</option>
                </select>
              </div>
              <!-- Free Plan Fields -->
              <div v-if="newPricing.planType === 'FREE'">
                <div class="form-group">
                  <label>Period Unit</label>
                  <select v-model="newPricing.freeAccessPeriod.periodUnit" class="form-control">
                    <option value="DAY">Day</option>
                    <option value="WEEK">Week</option>
                    <option value="MONTH">Month</option>
                    <option value="YEAR">Year</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Period</label>
                  <input v-model.number="newPricing.freeAccessPeriod.period" type="number" class="form-control" placeholder="Enter period">
                </div>
              </div>
              <!-- Paid Plan Fields -->
              <div v-if="newPricing.planType === 'PAID'">
                <div class="form-group">
                  <label>Billing Type</label>
                  <select v-model="newPricing.billingDetails.billingType" class="form-control" @change="updateBillingType($event)">
                    <option value="ONCE">Once</option>
                    <option value="RECURRING">Recurring</option>
                    <option value="TIERED">Tiered</option>
                  </select>
                </div>
                <!-- ONCE Billing Type -->
                <div v-if="newPricing.billingDetails.billingType === 'ONCE'">
                  <div v-for="(charge, index) in newPricing.billingDetails.oneOffCharges" :key="index" class="form-group">
                    <label>Charge {{ index + 1 }}</label>
                    <div class="row">
                      <div class="col-5">
                        <input v-model.number="charge.amount" type="number" class="form-control" placeholder="Enter amount">
                      </div>
                      <div class="col-5">
                        <input v-model="charge.label" type="text" class="form-control" placeholder="Enter label">
                      </div>
                      <div class="col-2">
                        <button type="button" class="btn btn-danger" @click="removeCharge('oneOffCharges', index)">Remove</button>
                      </div>
                    </div>
                  </div>
                  <button type="button" class="btn btn-secondary" @click="addCharge('oneOffCharges')">Add Charge</button>
                </div>
                <!-- RECURRING Billing Type -->
                <div v-if="newPricing.billingDetails.billingType === 'RECURRING'">
                  <div v-for="(charge, index) in newPricing.billingDetails.recurring" :key="index" class="form-group">
                    <label>Charge {{ index + 1 }}</label>
                    <div class="row">
                      <div class="col-4">
                        <input v-model.number="charge.charges.amount" type="number" class="form-control" placeholder="Enter amount">
                      </div>
                      <div class="col-4">
                        <input v-model="charge.charges.label" type="text" class="form-control" placeholder="Enter label">
                      </div>
                      <div class="col-4">
                        <select v-model="charge.chargeBasis" class="form-control">
                          <option value="PER_USER">Per User</option>
                          <option value="PER_POLICY">Per Policy</option>
                          <option value="PER_PERIOD">Per Period</option>
                        </select>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-5">
                        <select v-model="charge.frequency.periodUnit" class="form-control">
                          <option value="DAY">Day</option>
                          <option value="WEEK">Week</option>
                          <option value="MONTH">Month</option>
                          <option value="YEAR">Year</option>
                        </select>
                      </div>
                      <div class="col-5">
                        <input v-model.number="charge.frequency.period" type="number" class="form-control" placeholder="Enter period">
                      </div>
                      <div class="col-2">
                        <button type="button" class="btn btn-danger" @click="removeCharge('recurring', index)">Remove</button>
                      </div>
                    </div>
                  </div>
                  <button type="button" class="btn btn-secondary" @click="addCharge('recurring')">Add Charge</button>
                </div>
                <!-- TIERED Billing Type -->
                <div v-if="newPricing.billingDetails.billingType === 'TIERED'">
                  <h6>Intro Charges</h6>
                  <div v-for="(charge, index) in newPricing.billingDetails.tiered.intro" :key="'intro-' + index" class="form-group">
                    <label>Intro Charge {{ index + 1 }}</label>
                    <div class="row">
                      <div class="col-4">
                        <input v-model.number="charge.charges.amount" type="number" class="form-control" placeholder="Enter amount">
                      </div>
                      <div class="col-4">
                        <input v-model="charge.charges.label" type="text" class="form-control" placeholder="Enter label">
                      </div>
                      <div class="col-4">
                        <select v-model="charge.chargeBasis" class="form-control">
                          <option value="PER_USER">Per User</option>
                          <option value="PER_POLICY">Per Policy</option>
                          <option value="PER_PERIOD">Per Period</option>
                        </select>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-5">
                        <select v-model="charge.period.periodUnit" class="form-control">
                          <option value="DAY">Day</option>
                          <option value="WEEK">Week</option>
                          <option value="MONTH">Month</option>
                          <option value="YEAR">Year</option>
                        </select>
                      </div>
                      <div class="col-5">
                        <input v-model.number="charge.period.period" type="number" class="form-control" placeholder="Enter period">
                      </div>
                      <div class="col-2">
                        <button type="button" class="btn btn-danger" @click="removeCharge('tiered.intro', index)">Remove</button>
                      </div>
                    </div>
                  </div>
                  <button type="button" class="btn btn-secondary" @click="addCharge('tiered.intro')">Add Intro Charge</button>
                  <h6>After Charges</h6>
                  <div v-for="(charge, index) in newPricing.billingDetails.tiered.after" :key="'after-' + index" class="form-group">
                    <label>After Charge {{ index + 1 }}</label>
                    <div class="row">
                      <div class="col-4">
                        <input v-model.number="charge.charges.amount" type="number" class="form-control" placeholder="Enter amount">
                      </div>
                      <div class="col-4">
                        <input v-model="charge.charges.label" type="text" class="form-control" placeholder="Enter label">
                      </div>
                      <div class="col-4">
                        <select v-model="charge.chargeBasis" class="form-control">
                          <option value="PER_USER">Per User</option>
                          <option value="PER_POLICY">Per Policy</option>
                          <option value="PER_PERIOD">Per Period</option>
                        </select>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-5">
                        <select v-model="charge.frequency.periodUnit" class="form-control">
                          <option value="DAY">Day</option>
                          <option value="WEEK">Week</option>
                          <option value="MONTH">Month</option>
                          <option value="YEAR">Year</option>
                        </select>
                      </div>
                      <div class="col-5">
                        <input v-model.number="charge.frequency.period" type="number" class="form-control" placeholder="Enter period">
                      </div>
                      <div class="col-2">
                        <button type="button" class="btn btn-danger" @click="removeCharge('tiered.after', index)">Remove</button>
                      </div>
                    </div>
                  </div>
                  <button type="button" class="btn btn-secondary" @click="addCharge('tiered.after')">Add After Charge</button>
                </div>
              </div>
              <div class="form-group">
                <label>Is Renewable</label>
                <div class="radio">
                  <label><input type="radio" v-model="newPricing.isRenewable" :value="true"> True</label>
                </div>
                <div class="radio">
                  <label><input type="radio" v-model="newPricing.isRenewable" :value="false"> False</label>
                </div>
              </div>
              <div class="form-group">
                <label>Is Discountable</label>
                <div class="radio">
                  <label><input type="radio" v-model="newPricing.isDiscountable" :value="true"> True</label>
                </div>
                <div class="radio">
                  <label><input type="radio" v-model="newPricing.isDiscountable" :value="false"> False</label>
                </div>
                <div v-if="newPricing.isDiscountable" class="form-group">
                  <label>Plan Discount</label>
                  <input v-model.number="newPricing.planDiscount" type="number" class="form-control" placeholder="Enter discount amount">
                </div>
              </div>
              <div class="form-group">
                <label>Features</label>
                <textarea v-model="newPricing.features" class="form-control" rows="3" placeholder="Enter features, separated by commas"></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeCreateModal">Close</button>
            <button type="button" class="btn btn-primary" @click="createPricing">Save</button>
          </div>
        </div>
      </div>
    </div>

    <!-- View Modal -->
    <div
      class="modal fade"
      :class="{ show: showViewModal }"
      :style="{ display: showViewModal ? 'block' : 'none' }"
      tabindex="-1"
      role="dialog"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">View Pricing</h5>
            <button type="button" class="close" @click="closeViewModal">&times;</button>
          </div>
          <div class="modal-body">
            <p><strong>Plan Name:</strong> {{ currentItem.planName }}</p>
            <p><strong>Description:</strong> {{ currentItem.planDescription }}</p>
            <p><strong>Plan Type:</strong> {{ currentItem.planType }}</p>
            <p><strong>Entity:</strong> {{ currentItem.planUserType }}</p>
            <div v-if="currentItem.planType === 'FREE'">
              <p><strong>Period Unit:</strong> {{ currentItem.freeAccessPeriod?.periodUnit }}</p>
              <p><strong>Period:</strong> {{ currentItem.freeAccessPeriod?.period }}</p>
            </div>
            <div v-if="currentItem.planType === 'PAID'">
              <p><strong>Billing Type:</strong> {{ currentItem.billingDetails?.billingType }}</p>
              <div v-if="currentItem.billingDetails?.billingType === 'ONCE'">
                <p><strong>Charges:</strong></p>
                <ul>
                  <li v-for="(charge, index) in currentItem.billingDetails.oneOffCharges" :key="index">
                    {{ charge.label }}: {{ charge.amount }}
                  </li>
                </ul>
              </div>
              <div v-if="currentItem.billingDetails?.billingType === 'RECURRING'">
                <p><strong>Charges:</strong></p>
                <ul>
                  <li v-for="(charge, index) in currentItem.billingDetails.recurring" :key="index">
                    {{ charge.charges.label }}: {{ charge.charges.amount }} ({{ charge.chargeBasis }}) every {{ charge.frequency.period }} {{ charge.frequency.periodUnit }}(s)
                  </li>
                </ul>
              </div>
              <div v-if="currentItem.billingDetails?.billingType === 'TIERED'">
                <p><strong>Intro Charges:</strong></p>
                <ul>
                  <li v-for="(charge, index) in currentItem.billingDetails.tiered.intro" :key="'intro-' + index">
                    {{ charge.charges.label }}: {{ charge.charges.amount }} ({{ charge.chargeBasis }}) for {{ charge.period.period }} {{ charge.period.periodUnit }}(s)
                  </li>
                </ul>
                <p><strong>After Charges:</strong></p>
                <ul>
                  <li v-for="(charge, index) in currentItem.billingDetails.tiered.after" :key="'after-' + index">
                    {{ charge.charges.label }}: {{ charge.charges.amount }} ({{ charge.chargeBasis }}) every {{ charge.frequency.period }} {{ charge.frequency.periodUnit }}(s)
                  </li>
                </ul>
              </div>
            </div>
            <p><strong>Is Renewable:</strong> {{ currentItem.isRenewable }}</p>
            <p><strong>Is Discountable:</strong> {{ currentItem.isDiscountable }}</p>
            <p v-if="currentItem.isDiscountable"><strong>Plan Discount:</strong> {{ currentItem.planDiscount }}</p>
            <p><strong>Features:</strong> {{ currentItem.features }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeViewModal">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div
      class="modal fade"
      :class="{ show: showEditModal }"
      :style="{ display: showEditModal ? 'block' : 'none' }"
      tabindex="-1"
      role="dialog"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Edit Pricing</h5>
            <button type="button" class="close" @click="closeEditModal">&times;</button>
          </div>
          <div class="modal-body">
            <form>
              <div class="form-group">
                <label>Plan Name</label>
                <input v-model="currentItem.planName" type="text" class="form-control" placeholder="Enter plan name">
              </div>
              <div class="form-group">
                <label>Description</label>
                <textarea v-model="currentItem.planDescription" class="form-control" rows="3" placeholder="Enter description"></textarea>
              </div>
              <div class="form-group">
                <label>Plan Type</label>
                <select v-model="currentItem.planType" class="form-control" @change="updatePlanType('edit')">
                  <option value="FREE">Free</option>
                  <option value="PAID">Paid</option>
                </select>
              </div>
              <div class="form-group">
                <label>Entity</label>
                <select v-model="currentItem.planUserType" class="form-control">
                  <option value="INTERMEDIARY">Intermediary</option>
                  <option value="UNDERWRITER">Underwriter</option>
                  <option value="CLIENT">Client</option>
                  <option value="SERVICE_PROVIDER">Service Provider</option>
                </select>
              </div>
              <!-- Free Plan Fields -->
              <div v-if="currentItem.planType === 'FREE'">
                <div class="form-group">
                  <label>Period Unit</label>
                  <select v-model="currentItem.freeAccessPeriod.periodUnit" class="form-control">
                    <option value="DAY">Day</option>
                    <option value="WEEK">Week</option>
                    <option value="MONTH">Month</option>
                    <option value="YEAR">Year</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Period</label>
                  <input v-model.number="currentItem.freeAccessPeriod.period" type="number" class="form-control" placeholder="Enter period">
                </div>
              </div>
              <!-- Paid Plan Fields -->
              <div v-if="currentItem.planType === 'PAID'">
                <div class="form-group">
                  <label>Billing Type</label>
                  <select v-model="currentItem.billingDetails.billingType" class="form-control" @change="updateBillingType($event, 'edit')">
                    <option value="ONCE">Once</option>
                    <option value="RECURRING">Recurring</option>
                    <option value="TIERED">Tiered</option>
                  </select>
                </div>
                <!-- ONCE Billing Type -->
                <div v-if="currentItem.billingDetails.billingType === 'ONCE'">
                  <div v-for="(charge, index) in currentItem.billingDetails.oneOffCharges" :key="index" class="form-group">
                    <label>Charge {{ index + 1 }}</label>
                    <div class="row">
                      <div class="col-5">
                        <input v-model.number="charge.amount" type="number" class="form-control" placeholder="Enter amount">
                      </div>
                      <div class="col-5">
                        <input v-model="charge.label" type="text" class="form-control" placeholder="Enter label">
                      </div>
                      <div class="col-2">
                        <button type="button" class="btn btn-danger" @click="removeCharge('oneOffCharges', index, 'edit')">Remove</button>
                      </div>
                    </div>
                  </div>
                  <button type="button" class="btn btn-secondary" @click="addCharge('oneOffCharges', 'edit')">Add Charge</button>
                </div>
                <!-- RECURRING Billing Type -->
                <div v-if="currentItem.billingDetails.billingType === 'RECURRING'">
                  <div v-for="(charge, index) in currentItem.billingDetails.recurring" :key="index" class="form-group">
                    <label>Charge {{ index + 1 }}</label>
                    <div class="row">
                      <div class="col-4">
                        <input v-model.number="charge.charges.amount" type="number" class="form-control" placeholder="Enter amount">
                      </div>
                      <div class="col-4">
                        <input v-model="charge.charges.label" type="text" class="form-control" placeholder="Enter label">
                      </div>
                      <div class="col-4">
                        <select v-model="charge.chargeBasis" class="form-control">
                          <option value="PER_USER">Per User</option>
                          <option value="PER_POLICY">Per Policy</option>
                          <option value="PER_PERIOD">Per Period</option>
                        </select>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-5">
                        <select v-model="charge.frequency.periodUnit" class="form-control">
                          <option value="DAY">Day</option>
                          <option value="WEEK">Week</option>
                          <option value="MONTH">Month</option>
                          <option value="YEAR">Year</option>
                        </select>
                      </div>
                      <div class="col-5">
                        <input v-model.number="charge.frequency.period" type="number" class="form-control" placeholder="Enter period">
                      </div>
                      <div class="col-2">
                        <button type="button" class="btn btn-danger" @click="removeCharge('recurring', index, 'edit')">Remove</button>
                      </div>
                    </div>
                  </div>
                  <button type="button" class="btn btn-secondary" @click="addCharge('recurring', 'edit')">Add Charge</button>
                </div>
                <!-- TIERED Billing Type -->
                <div v-if="currentItem.billingDetails.billingType === 'TIERED'">
                  <h6>Intro Charges</h6>
                  <div v-for="(charge, index) in currentItem.billingDetails.tiered.intro" :key="'intro-' + index" class="form-group">
                    <label>Intro Charge {{ index + 1 }}</label>
                    <div class="row">
                      <div class="col-4">
                        <input v-model.number="charge.charges.amount" type="number" class="form-control" placeholder="Enter amount">
                      </div>
                      <div class="col-4">
                        <input v-model="charge.charges.label" type="text" class="form-control" placeholder="Enter label">
                      </div>
                      <div class="col-4">
                        <select v-model="charge.chargeBasis" class="form-control">
                          <option value="PER_USER">Per User</option>
                          <option value="PER_POLICY">Per Policy</option>
                          <option value="PER_PERIOD">Per Period</option>
                        </select>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-5">
                        <select v-model="charge.period.periodUnit" class="form-control">
                          <option value="DAY">Day</option>
                          <option value="WEEK">Week</option>
                          <option value="MONTH">Month</option>
                          <option value="YEAR">Year</option>
                        </select>
                      </div>
                      <div class="col-5">
                        <input v-model.number="charge.period.period" type="number" class="form-control" placeholder="Enter period">
                      </div>
                      <div class="col-2">
                        <button type="button" class="btn btn-danger" @click="removeCharge('tiered.intro', index, 'edit')">Remove</button>
                      </div>
                    </div>
                  </div>
                  <button type="button" class="btn btn-secondary" @click="addCharge('tiered.intro', 'edit')">Add Intro Charge</button>
                  <h6>After Charges</h6>
                  <div v-for="(charge, index) in currentItem.billingDetails.tiered.after" :key="'after-' + index" class="form-group">
                    <label>After Charge {{ index + 1 }}</label>
                    <div class="row">
                      <div class="col-4">
                        <input v-model.number="charge.charges.amount" type="number" class="form-control" placeholder="Enter amount">
                      </div>
                      <div class="col-4">
                        <input v-model="charge.charges.label" type="text" class="form-control" placeholder="Enter label">
                      </div>
                      <div class="col-4">
                        <select v-model="charge.chargeBasis" class="form-control">
                          <option value="PER_USER">Per User</option>
                          <option value="PER_POLICY">Per Policy</option>
                          <option value="PER_PERIOD">Per Period</option>
                        </select>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-5">
                        <select v-model="charge.frequency.periodUnit" class="form-control">
                          <option value="DAY">Day</option>
                          <option value="WEEK">Week</option>
                          <option value="MONTH">Month</option>
                          <option value="YEAR">Year</option>
                        </select>
                      </div>
                      <div class="col-5">
                        <input v-model.number="charge.frequency.period" type="number" class="form-control" placeholder="Enter period">
                      </div>
                      <div class="col-2">
                        <button type="button" class="btn btn-danger" @click="removeCharge('tiered.after', index, 'edit')">Remove</button>
                      </div>
                    </div>
                  </div>
                  <button type="button" class="btn btn-secondary" @click="addCharge('tiered.after', 'edit')">Add After Charge</button>
                </div>
              </div>
              <div class="form-group">
                <label>Is Renewable</label>
                <div class="radio">
                  <label><input type="radio" v-model="currentItem.isRenewable" :value="true"> True</label>
                </div>
                <div class="radio">
                  <label><input type="radio" v-model="currentItem.isRenewable" :value="false"> False</label>
                </div>
              </div>
              <div class="form-group">
                <label>Is Discountable</label>
                <div class="radio">
                  <label><input type="radio" v-model="currentItem.isDiscountable" :value="true"> True</label>
                </div>
                <div class="radio">
                  <label><input type="radio" v-model="currentItem.isDiscountable" :value="false"> False</label>
                </div>
                <div v-if="currentItem.isDiscountable" class="form-group">
                  <label>Plan Discount</label>
                  <input v-model.number="currentItem.planDiscount" type="number" class="form-control" placeholder="Enter discount amount">
                </div>
              </div>
              <div class="form-group">
                <label>Features</label>
                <textarea v-model="currentItem.features" class="form-control" rows="3" placeholder="Enter features, separated by commas"></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEditModal">Close</button>
            <button type="button" class="btn btn-primary" @click="updatePricing">Save Changes</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Backdrop for modals -->
    <div
      class="modal-backdrop fade"
      :class="{ show: showCreateModal || showViewModal || showEditModal }"
      v-if="showCreateModal || showViewModal || showEditModal"
      @click="closeAllModals"
    ></div>
  </div>
</template>

<script>
export default {
  created() {
    this.pricingPlans = Models.PricingPlans;
  },
  data() {
    return {
      pricingData: [],
      pricingPlans: {},
      showCreateModal: false,
      showViewModal: false,
      showEditModal: false,
      newPricing: {
        planName: '',
        planDescription: '',
        description: '',
        planType: 'FREE',
        planUserType: 'INTERMEDIARY',
        isRenewable: false,
        isCancelable: true,
        planDiscount: 0,
        isDiscountable: false,
        freeAccessPeriod: { periodUnit: 'MONTH', period: 1 },
        billingDetails: { billingType: 'ONCE', currency: 'USD', oneOffCharges: [{ amount: 0, label: '' }] },
        features: '',
        access: { modules: [], routes: [], components: [] }
      },
      currentItem: {},
      editIndex: -1
    };
  },
  methods: {
    updatePlanType(mode = 'create') {
      const item = mode === 'create' ? this.newPricing : this.currentItem;
      console.log('updatePlanType called for mode:', mode, 'new planType:', item.planType);
      if (item.planType === 'FREE') {
        item.billingDetails = undefined;
        item.freeAccessPeriod = { periodUnit: 'MONTH', period: 1 };
      } else {
        item.freeAccessPeriod = undefined;
        item.billingDetails = { billingType: 'ONCE', currency: 'USD', oneOffCharges: [{ amount: 0, label: '' }] };
      }
      console.log('After updatePlanType:', JSON.parse(JSON.stringify(item)));
    },
    updateBillingType(event, mode = 'create') {
      const item = mode === 'create' ? this.newPricing : this.currentItem;
      const billingType = event.target.value;
      console.log('updateBillingType called for mode:', mode, 'new billingType:', billingType, 'current billingDetails:', JSON.parse(JSON.stringify(item.billingDetails)));

      if (billingType === 'ONCE') {
        this.$set(item, 'billingDetails', {
          billingType: 'ONCE',
          currency: 'USD',
          oneOffCharges: [{ amount: 0, label: '' }]
        });
      } else if (billingType === 'RECURRING') {
        this.$set(item, 'billingDetails', {
          billingType: 'RECURRING',
          currency: 'USD',
          recurring: [
            {
              charges: { amount: 0, label: '' },
              frequency: { periodUnit: 'MONTH', period: 1 },
              chargeBasis: 'PER_PERIOD'
            }
          ]
        });
      } else if (billingType === 'TIERED') {
        this.$set(item, 'billingDetails', {
          billingType: 'TIERED',
          currency: 'USD',
          tiered: {
            intro: [
              {
                charges: { amount: 0, label: '' },
                period: { periodUnit: 'MONTH', period: 1 },
                chargeBasis: 'PER_PERIOD'
              }
            ],
            after: [
              {
                charges: { amount: 0, label: '' },
                frequency: { periodUnit: 'MONTH', period: 1 },
                chargeBasis: 'PER_PERIOD'
              }
            ]
          }
        });
      }
      console.log('After updateBillingType:', JSON.parse(JSON.stringify(item.billingDetails)));
    },
    addCharge(type, mode = 'create') {
      const item = mode === 'create' ? this.newPricing : this.currentItem;
      console.log('addCharge called for type:', type, 'mode:', mode, 'current billingDetails:', JSON.parse(JSON.stringify(item.billingDetails)));
      if (type === 'oneOffCharges') {
        item.billingDetails.oneOffCharges.push({ amount: 0, label: '' });
        console.log('After adding oneOffCharges:', JSON.parse(JSON.stringify(item.billingDetails.oneOffCharges)));
      } else if (type === 'recurring') {
        item.billingDetails.recurring.push({ charges: { amount: 0, label: '' }, frequency: { periodUnit: 'MONTH', period: 1 }, chargeBasis: 'PER_PERIOD' });
        console.log('After adding recurring:', JSON.parse(JSON.stringify(item.billingDetails.recurring)));
      } else if (type === 'tiered.intro') {
        item.billingDetails.tiered.intro.push({ charges: { amount: 0, label: '' }, period: { periodUnit: 'MONTH', period: 1 }, chargeBasis: 'PER_PERIOD' });
        console.log('After adding tiered.intro:', JSON.parse(JSON.stringify(item.billingDetails.tiered.intro)));
      } else if (type === 'tiered.after') {
        item.billingDetails.tiered.after.push({ charges: { amount: 0, label: '' }, frequency: { periodUnit: 'MONTH', period: 1 }, chargeBasis: 'PER_PERIOD' });
        console.log('After adding tiered.after:', JSON.parse(JSON.stringify(item.billingDetails.tiered.after)));
      }
    },
    removeCharge(type, index, mode = 'create') {
      const item = mode === 'create' ? this.newPricing : this.currentItem;
      console.log('removeCharge called for type:', type, 'index:', index, 'mode:', mode, 'current billingDetails:', JSON.parse(JSON.stringify(item.billingDetails)));
      if (type === 'oneOffCharges' && item.billingDetails.oneOffCharges.length > 1) {
        item.billingDetails.oneOffCharges.splice(index, 1);
        console.log('After removing oneOffCharges:', JSON.parse(JSON.stringify(item.billingDetails.oneOffCharges)));
      } else if (type === 'recurring' && item.billingDetails.recurring.length > 1) {
        item.billingDetails.recurring.splice(index, 1);
        console.log('After removing recurring:', JSON.parse(JSON.stringify(item.billingDetails.recurring)));
      } else if (type === 'tiered.intro' && item.billingDetails.tiered.intro.length > 1) {
        item.billingDetails.tiered.intro.splice(index, 1);
        console.log('After removing tiered.intro:', JSON.parse(JSON.stringify(item.billingDetails.tiered.intro)));
      } else if (type === 'tiered.after' && item.billingDetails.tiered.after.length > 1) {
        item.billingDetails.tiered.after.splice(index, 1);
        console.log('After removing tiered.after:', JSON.parse(JSON.stringify(item.billingDetails.tiered.after)));
      }
    },
    openCreateModal() {
      this.showCreateModal = true;
      this.newPricing = {
        planName: '',
        planDescription: '',
        description: '',
        planType: 'FREE',
        planUserType: 'INTERMEDIARY',
        isRenewable: false,
        isCancelable: true,
        planDiscount: 0,
        isDiscountable: false,
        freeAccessPeriod: { periodUnit: 'MONTH', period: 1 },
        billingDetails: { billingType: 'ONCE', currency: 'USD', oneOffCharges: [{ amount: 0, label: '' }] },
        features: '',
        access: { modules: [], routes: [], components: [] }
      };
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },
    async createPricing() {
      try {
        const response = await this.addPricing(this.newPricing);
        if (response.success) {
          this.pricingData.push({ ...this.newPricing });
          this.closeCreateModal();
        } else {
          console.error('Failed to create pricing:', response.error);
        }
      } catch (error) {
        console.error('Error creating pricing:', error);
      }
    },
    openViewModal(item) {
      this.currentItem = { ...item };
      this.showViewModal = true;
    },
    closeViewModal() {
      this.showViewModal = false;
    },
    openEditModal(item) {
      this.currentItem = { ...item };
      this.editIndex = this.pricingData.findIndex(p => p === item);
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    async updatePricing() {
      try {
        if (this.editIndex !== -1) {
          const response = await this.addPricing(this.currentItem);
          if (response.success) {
            this.$set(this.pricingData, this.editIndex, { ...this.currentItem });
            this.closeEditModal();
          } else {
            console.error('Failed to update pricing:', response.error);
          }
        }
      } catch (error) {
        console.error('Error updating pricing:', error);
      }
    },
    deletePricing(index) {
      if (confirm('Are you sure you want to delete this pricing?')) {
        this.pricingData.splice(index, 1);
      }
    },
    closeAllModals() {
      this.showCreateModal = false;
      this.showViewModal = false;
      this.showEditModal = false;
    },
    async addPricing(pricing) {
      console.log("adding pricing...", pricing);
      try {
        const data = {
          planName: pricing.planName,
          planDescription: pricing.planDescription,
          description: pricing.planDescription,
          planType: pricing.planType.toUpperCase(),
          planUserType: pricing.planUserType.toUpperCase(),
          isRenewable: pricing.isRenewable,
          isCancelable: pricing.isCancelable,
          planDiscount: pricing.planDiscount,
          isDiscountable: pricing.isDiscountable,
          access: {
            modules: pricing.features.split(',').map(f => f.trim().toLowerCase()),
            routes: [],
            components: []
          }
        };
        if (pricing.planType === 'FREE') {
          data.freeAccessPeriod = pricing.freeAccessPeriod;
        } else {
          data.billingDetails = {
            ...pricing.billingDetails,
            trialPeriod: { period: 0, periodUnit: 'DAY' },
            gracePeriod: { period: 0, periodUnit: 'DAY' }
          };
        }
        const res = await req.callServerSecureV2({
          service: { name: 'underwriting', version: 'v1' },
          resource: { name: 'plan-management', version: 'v1' },
          action: { name: 'createPlan', version: 'v1' },
          data
        });
        await this.fetchPricing();
        return { success: true, data: res };
      } catch (error) {
        return { success: false, error };
      }
    },
    async fetchPricing() {
      console.log("fetching pricing plans...");
      try {
        const res = await req.callServerSecureV2({
          service: { name: 'underwriting', version: 'v1' },
          resource: { name: 'plan-management', version: 'v1' },
          action: { name: 'listPlans', version: 'v1' },
          data: {}
        });
        console.log("res==", res.data.result.data);
        this.pricingData = res.data.result.data.map(plan => ({
          ...plan,
          features: plan.access.modules.join(', ')
        }));
        return { success: true, data: this.pricingData };
      } catch (error) {
        return { success: false, error };
      }
    }
  },
  async mounted() {
    Event.$emit('loader:show', false);
    await this.fetchPricing();
  }
};
</script>

<style lang="less">
.table-modern {
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #dee2e6;
  }
  th {
    background-color: #f8f9fa;
    font-weight: 600;
  }
  tr:hover {
    background-color: #f1f3f5;
  }
}
.modal-backdrop {
  display: none;
  &.show {
    display: block;
  }
}
.modal.show {
  display: block;
}
</style>